import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  Loader2,
  RefreshCw,
  Save,
  Trash2,
  XCircle,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { ReceiptsApi, errorMessage } from "../lib/api";
import { formatDate, formatDateTime, formatMoney, formatNumber } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import StatusBadge, { ConfidenceMeter, PayoutBadge } from "./StatusBadge";
import { useI18n } from "../i18n";
import { useAuth } from "../context/AuthContext";

const FIELDS = [
  { key: "vendor_name", labelKey: "drawer.vendorField" },
  { key: "vendor_tax_id", labelKey: "drawer.taxIdField" },
  { key: "receipt_number", labelKey: "drawer.numberField" },
  { key: "issue_date", labelKey: "drawer.dateField", type: "date" },
  { key: "payment_method", labelKey: "drawer.paymentField" },
];

const NUMERIC = [
  { key: "net_total", labelKey: "drawer.netField" },
  { key: "vat_total", labelKey: "drawer.vatField" },
  { key: "gross_total", labelKey: "drawer.grossField" },
];

/** Receipt review panel: image next to editable extracted data + VAT recap + actions. */
export const ReceiptDetailDrawer = ({
  receiptId,
  open,
  onClose,
  onChanged,
  isAdmin,
  categories = [],
  currency = "EUR",
}) => {
  const { t } = useI18n();
  const { user, isAccountingFirm } = useAuth();
  const [receipt, setReceipt] = useState(null);
  const [auditTrail, setAuditTrail] = useState([]);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const readOnly = user?.role === "accountant";

  const load = useCallback(async () => {
    if (!receiptId) return;
    setLoading(true);
    try {
      const data = await ReceiptsApi.get(receiptId);
      setReceipt(data.receipt);
      setAuditTrail(data.audit || []);
      setForm({
        vendor_name: data.receipt.vendor_name || "",
        vendor_tax_id: data.receipt.vendor_tax_id || "",
        receipt_number: data.receipt.receipt_number || "",
        issue_date: data.receipt.issue_date || "",
        payment_method: data.receipt.payment_method || "",
        category: data.receipt.category || "other",
        net_total: data.receipt.net_total ?? "",
        vat_total: data.receipt.vat_total ?? "",
        gross_total: data.receipt.gross_total ?? "",
        notes: data.receipt.notes || "",
      });
    } catch (e) {
      toast.error(errorMessage(e, "Could not load the receipt"));
    } finally {
      setLoading(false);
    }
  }, [receiptId]);

  useEffect(() => {
    if (open && receiptId) load();
  }, [open, receiptId, load]);

  useEffect(() => {
    let url;
    let cancelled = false;
    if (open && receiptId) {
      ReceiptsApi.image(receiptId)
        .then((blob) => {
          if (cancelled) return;
          url = URL.createObjectURL(blob);
          setImageUrl(url);
        })
        .catch(() => setImageUrl(null));
    }
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
      setImageUrl(null);
    };
  }, [open, receiptId]);

  const dirty = useMemo(() => {
    if (!receipt) return false;
    return (
      form.vendor_name !== (receipt.vendor_name || "") ||
      form.vendor_tax_id !== (receipt.vendor_tax_id || "") ||
      form.receipt_number !== (receipt.receipt_number || "") ||
      form.issue_date !== (receipt.issue_date || "") ||
      form.payment_method !== (receipt.payment_method || "") ||
      form.category !== (receipt.category || "other") ||
      String(form.net_total) !== String(receipt.net_total ?? "") ||
      String(form.vat_total) !== String(receipt.vat_total ?? "") ||
      String(form.gross_total) !== String(receipt.gross_total ?? "") ||
      form.notes !== (receipt.notes || "")
    );
  }, [form, receipt]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        vendor_name: form.vendor_name || null,
        vendor_tax_id: form.vendor_tax_id || null,
        receipt_number: form.receipt_number || null,
        issue_date: form.issue_date || null,
        payment_method: form.payment_method || null,
        category: form.category || null,
        notes: form.notes || null,
      };
      ["net_total", "vat_total", "gross_total"].forEach((k) => {
        if (form[k] !== "" && form[k] !== null && !Number.isNaN(Number(form[k]))) {
          payload[k] = Number(form[k]);
        }
      });
      const data = await ReceiptsApi.update(receiptId, payload);
      setReceipt(data.receipt);
      toast.success(t("drawer.savedToast"), {
        description: `${t("drawer.refundRecalculated")}: ${formatMoney(
          data.receipt?.refund?.amount,
          data.receipt?.currency || currency
        )}`,
      });
      onChanged && onChanged(data.receipt);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Could not save changes"));
    } finally {
      setSaving(false);
    }
  };

  const act = async (fn, message) => {
    try {
      const data = await fn();
      toast.success(message);
      if (data?.receipt) setReceipt(data.receipt);
      onChanged && onChanged(data?.receipt);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Action failed"));
    }
  };

  const cur = receipt?.currency || currency;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="ll-scroll w-full overflow-y-auto p-0 sm:max-w-3xl"
        data-testid="receipt-detail-drawer"
      >
        <SheetHeader className="sticky top-0 z-10 border-b bg-card px-5 py-4">
          <div className="flex items-start justify-between gap-4 pr-8">
            <div className="min-w-0">
              <SheetTitle className="truncate font-display text-lg" data-testid="receipt-detail-vendor">
                {receipt?.vendor_name || "Receipt"}
              </SheetTitle>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {receipt?.user_name} · {t("drawer.uploaded")} {formatDateTime(receipt?.created_at)}
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1.5">
              {receipt ? <StatusBadge status={receipt.status} /> : null}
              {receipt?.confidence != null ? <ConfidenceMeter value={receipt.confidence} /> : null}
            </div>
          </div>
        </SheetHeader>

        {loading && !receipt ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : null}

        {receipt ? (
          <div className="grid gap-5 p-5 lg:grid-cols-[minmax(0,300px)_minmax(0,1fr)]">
            <div className="space-y-3">
              <div className="overflow-hidden rounded-xl border bg-muted/40">
                {imageUrl ? (
                  <a href={imageUrl} target="_blank" rel="noreferrer">
                    <img
                      src={imageUrl}
                      alt={`Receipt from ${receipt.vendor_name || "vendor"}`}
                      className="max-h-[420px] w-full object-contain"
                      data-testid="receipt-detail-image"
                    />
                  </a>
                ) : (
                  <div className="flex h-56 items-center justify-center text-sm text-muted-foreground">
                    {t("drawer.loadingImage")}
                  </div>
                )}
              </div>
              <div className="ll-card p-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">
                  {t("drawer.employeeRefund")}
                </div>
                <div className="ll-num mt-1 font-display text-2xl font-semibold text-primary" data-testid="receipt-detail-refund">
                  {formatMoney(receipt.refund?.amount, cur)}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {receipt.refund?.mode === "gross_percent"
                    ? t("drawer.ofGross", { pct: formatNumber(receipt.refund?.gross_percent, 0) })
                    : receipt.refund?.mode === "both"
                      ? t("drawer.both", {
                          vat: formatNumber(receipt.refund?.vat_percent, 0),
                          gross: formatNumber(receipt.refund?.gross_percent, 0),
                        })
                      : t("drawer.ofVat", { pct: formatNumber(receipt.refund?.vat_percent, 0) })}
                  {receipt.refund?.rule_source === "employee"
                    ? ` · ${t("drawer.employeeRule")}`
                    : receipt.refund?.rule_source?.startsWith("category")
                      ? ` · ${t("drawer.categoryRule")}`
                      : ""}
                  {receipt.refund?.capped ? ` · ${t("drawer.capped")}` : ""}
                </p>
              </div>
              {receipt.fuel_liters || receipt.odometer ? (
                <div className="ll-card p-3 text-sm">
                  {receipt.fuel_liters ? (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("drawer.litres")}</span>
                      <span className="ll-num">{formatNumber(receipt.fuel_liters, 2)}</span>
                    </div>
                  ) : null}
                  {receipt.odometer ? (
                    <div className="mt-1 flex justify-between">
                      <span className="text-muted-foreground">{t("drawer.odometer")}</span>
                      <span className="ll-num">{formatNumber(receipt.odometer, 0)} km</span>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="space-y-4">
              {receipt.warnings?.length || receipt.validation?.errors?.length ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="receipt-warnings">
                  <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
                    <AlertTriangle className="h-4 w-4" /> {t("drawer.checkBefore")}
                  </div>
                  <ul className="mt-1.5 list-inside list-disc space-y-0.5 text-xs text-amber-900">
                    {(receipt.validation?.errors || []).map((w, i) => (
                      <li key={`e${i}`}>{w}</li>
                    ))}
                    {(receipt.warnings || []).map((w, i) => (
                      <li key={`w${i}`}>{w}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                  <CheckCircle2 className="h-4 w-4" /> {t("drawer.vatReconciles")}
                </div>
              )}

              {receipt.duplicate_of ? (
                <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <Copy className="h-4 w-4" /> {t("drawer.duplicateWarning")}
                </div>
              ) : null}

              <div className="grid gap-3 sm:grid-cols-2">
                {FIELDS.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label htmlFor={`field-${f.key}`} className="text-xs">
                      {t(f.labelKey)}
                    </Label>
                    <Input
                      id={`field-${f.key}`}
                      type={f.type || "text"}
                      value={form[f.key] || ""}
                      disabled={readOnly}
                      onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                      data-testid={`receipt-field-${f.key}`}
                    />
                  </div>
                ))}
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("common.category")}</Label>
                  <Select
                    value={form.category || "other"}
                    disabled={readOnly}
                    onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}
                  >
                    <SelectTrigger data-testid="receipt-field-category">
                      <SelectValue placeholder={t("common.category")} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.key} value={c.key}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                {NUMERIC.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label htmlFor={`num-${f.key}`} className="text-xs">
                      {t(f.labelKey)} ({cur})
                    </Label>
                    <Input
                      id={`num-${f.key}`}
                      inputMode="decimal"
                      className="ll-num text-right"
                      value={form[f.key] ?? ""}
                      disabled={readOnly}
                      onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                      data-testid={`receipt-field-${f.key}`}
                    />
                  </div>
                ))}
              </div>

              <div>
                <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t("drawer.vatRecap")}
                </div>
                <div className="overflow-hidden rounded-xl border">
                  <Table data-testid="receipt-vat-table">
                    <TableHeader className="bg-muted/40">
                      <TableRow>
                        <TableHead className="h-9">{t("drawer.rate")}</TableHead>
                        <TableHead className="h-9 text-right">{t("common.net")}</TableHead>
                        <TableHead className="h-9 text-right">{t("common.vat")}</TableHead>
                        <TableHead className="h-9 text-right">{t("common.gross")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(receipt.vat_breakdown || []).length ? (
                        receipt.vat_breakdown.map((b, i) => (
                          <TableRow key={i}>
                            <TableCell className="ll-num font-medium">{formatNumber(b.rate, 0)}%</TableCell>
                            <TableCell className="ll-num text-right">{formatMoney(b.net, cur)}</TableCell>
                            <TableCell className="ll-num text-right">{formatMoney(b.vat, cur)}</TableCell>
                            <TableCell className="ll-num text-right">{formatMoney(b.gross, cur)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                            {t("drawer.noRates")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {receipt.line_items?.length ? (
                <details className="rounded-xl border bg-card p-3">
                  <summary className="cursor-pointer text-sm font-medium">
                    {t("drawer.lineItems")} ({receipt.line_items.length})
                  </summary>
                  <ul className="mt-2 space-y-1 text-sm">
                    {receipt.line_items.map((li, i) => (
                      <li key={i} className="flex justify-between gap-3 border-b py-1 last:border-0">
                        <span className="min-w-0 truncate">{li.description || "Item"}</span>
                        <span className="ll-num shrink-0 text-muted-foreground">
                          {li.vat_rate != null ? `${formatNumber(li.vat_rate, 0)}% · ` : ""}
                          {li.gross_amount != null ? formatMoney(li.gross_amount, cur) : "—"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}

              <div className="space-y-1.5">
                <Label htmlFor="receipt-notes" className="text-xs">
                  {t("drawer.internalNote")}
                </Label>
                <Textarea
                  id="receipt-notes"
                  rows={2}
                  value={form.notes || ""}
                  disabled={readOnly}
                  placeholder={t("drawer.notePlaceholder")}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  data-testid="receipt-field-notes"
                />
              </div>

              {readOnly ? (
                <div
                  className="rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground"
                  data-testid="drawer-readonly-notice"
                >
                  {t("drawer.readOnly")}
                </div>
              ) : (
              <div className="flex flex-wrap items-center gap-2 border-t pt-4">
                <Button onClick={save} disabled={!dirty || saving} data-testid="receipt-save-button">
                  {saving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  {t("drawer.saveChanges")}
                </Button>
                {isAdmin ? (
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => act(() => ReceiptsApi.approve(receiptId), t("drawer.approvedToast"))}
                      disabled={receipt.status === "approved"}
                      data-testid="receipt-approve-button"
                    >
                      <CheckCircle2 className="mr-2 h-4 w-4" /> {t("common.approve")}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() =>
                        act(
                          () => ReceiptsApi.reject(receiptId, "Rejected by admin"),
                          t("drawer.rejectedToast")
                        )
                      }
                      disabled={receipt.status === "rejected"}
                      data-testid="receipt-reject-button"
                    >
                      <XCircle className="mr-2 h-4 w-4" /> {t("common.reject")}
                    </Button>
                    {(receipt.refund?.amount || 0) > 0 && !isAccountingFirm ? (
                      receipt.payout_status === "paid" ? (
                        <Button
                          variant="ghost"
                          onClick={() =>
                            act(() => ReceiptsApi.unmarkPaid(receiptId), t("receipts.unpaidToast"))
                          }
                          data-testid="receipt-unmark-paid-button"
                        >
                          <Wallet className="mr-2 h-4 w-4" /> {t("receipts.markUnpaid")}
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          onClick={() =>
                            act(() => ReceiptsApi.markPaid(receiptId), t("receipts.paidToast"))
                          }
                          data-testid="receipt-mark-paid-button"
                        >
                          <Wallet className="mr-2 h-4 w-4" /> {t("receipts.markPaid")}
                        </Button>
                      )
                    ) : null}
                  </>
                ) : null}
                <Button
                  variant="ghost"
                  onClick={() => act(() => ReceiptsApi.reprocess(receiptId), t("drawer.rescanToast"))}
                  data-testid="receipt-reprocess-button"
                >
                  <RefreshCw className="mr-2 h-4 w-4" /> {t("common.rescan")}
                </Button>
                <Button
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  onClick={async () => {
                    try {
                      await ReceiptsApi.remove(receiptId);
                      toast.success(t("drawer.deletedToast"));
                      onChanged && onChanged(null);
                      onClose();
                    } catch (e) {
                      toast.error(errorMessage(e, "Could not delete"));
                    }
                  }}
                  data-testid="receipt-delete-button"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> {t("common.delete")}
                </Button>
              </div>
              )}

              {auditTrail.length ? (
                <div className="rounded-xl border bg-muted/30 p-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t("drawer.activity")}
                  </div>
                  <ul className="mt-2 space-y-1 font-mono text-xs text-muted-foreground">
                    {auditTrail.slice(0, 6).map((a) => (
                      <li key={a.id}>
                        {formatDateTime(a.created_at)} · {a.action} · {a.actor_name}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};

export default ReceiptDetailDrawer;
